import React, { useState, useEffect } from 'react'
import { router } from '@inertiajs/react'
import {
  CalendarIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  DownloadIcon,
  KeyRoundIcon,
  PlusIcon,
  SearchIcon,
  UserIcon,
  UsersIcon,
  XIcon,
} from 'lucide-react'

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Pagination, PaginationContent, PaginationEllipsis, PaginationItem } from '@/components/ui/pagination'
import { Badge } from '@/components/ui/badge'
import { AppHeader } from '@/components/app-header'
import { ResetPasswordDialog } from '@/components/reset-password-dialog'
import authService from '@/lib/auth'

interface Patient {
  id: number
  display_name: string
  legal_name: string
  phone_number: string
  email: string | null
  zip_code: string | null
  site_name: string
  site_id: number
  case_status: string | null
  case_status_label: string | null
  days_since_last_activity: number | null
  consents_complete: boolean
  created_at: string
  risk_level: string | null
  risk_level_label: string | null
  days_at_current_stage: number | null
  upcoming_appointments_count: number
  has_treatment_plan_instructions: boolean
}

interface Site {
  id: number
  name: string
}

interface PaginationData {
  page: number
  pages: number
  count: number
  from: number
  to: number
  prev: number | null
  next: number | null
}

interface Filters {
  search?: string | null
  site_id?: number | null
  sort?: string
  direction?: string
}

interface AdminPatientsIndexProps {
  auth: {
    user: {
      id: number
      name: string
      email: string
      admin: boolean
    }
  }
  patients: Patient[]
  pagination: PaginationData
  filters: Filters
  sites: Site[]
}

export default function AdminPatientsIndex({ auth, patients, pagination, filters, sites }: AdminPatientsIndexProps) {
  const [searchTerm, setSearchTerm] = useState(filters.search || '')
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false)
  const [patientToResetPassword, setPatientToResetPassword] = useState<Patient | null>(null)

  const handleResetPasswordClick = (patient: Patient, e: React.MouseEvent) => {
    e.stopPropagation()
    setPatientToResetPassword(patient)
    setResetPasswordDialogOpen(true)
  }

  // Debounced search with 300ms delay
  useEffect(() => {
    const timer = setTimeout(() => {
      const currentFilter = filters.search || ''
      if (searchTerm !== currentFilter) {
        handleSearch(searchTerm)
      }
    }, 300)

    return () => clearTimeout(timer)
  }, [searchTerm])

  const handleSearch = (search: string) => {
    router.get('/admin/patients', {
      search: search || undefined,
      site_id: filters.site_id,
      sort: filters.sort,
      direction: filters.direction,
    }, {
      preserveState: true,
      preserveScroll: true,
      only: ['patients', 'pagination', 'filters'],
    })
  }

  const handleFilterChange = (filterName: string, value: string | undefined) => {
    router.get('/admin/patients', {
      search: filters.search,
      site_id: filterName === 'site_id' ? value : filters.site_id,
      sort: filters.sort,
      direction: filters.direction,
    }, {
      preserveState: true,
      preserveScroll: true,
      only: ['patients', 'pagination', 'filters'],
    })
  }

  const handleSort = (column: string) => {
    const newDirection = filters.sort === column && filters.direction === 'asc' ? 'desc' : 'asc'
    router.get('/admin/patients', {
      search: filters.search,
      site_id: filters.site_id,
      sort: column,
      direction: newDirection,
    }, {
      preserveState: true,
      preserveScroll: true,
      only: ['patients', 'pagination', 'filters'],
    })
  }

  const handlePageChange = (page: number) => {
    router.get('/admin/patients', {
      search: filters.search,
      site_id: filters.site_id,
      sort: filters.sort,
      direction: filters.direction,
      page,
    }, {
      preserveState: true,
      preserveScroll: true,
      only: ['patients', 'pagination'],
    })
  }

  const clearFilters = () => {
    setSearchTerm('')
    router.get('/admin/patients', {}, {
      preserveState: true,
      preserveScroll: true,
      only: ['patients', 'pagination', 'filters'],
    })
  }

  const hasActiveFilters = filters.search || filters.site_id

  const handleExportCSV = async () => {
    const params = new URLSearchParams()
    if (filters.search) params.append('search', filters.search)
    if (filters.site_id) params.append('site_id', filters.site_id.toString())
    if (filters.sort) params.append('sort', filters.sort)
    if (filters.direction) params.append('direction', filters.direction)

    const url = `/admin/patients/export_csv?${params.toString()}`

    try {
      const response = await fetch(url, {
        headers: {
          ...authService.authHeaders()
        }
      })

      if (!response.ok) {
        throw new Error('Export failed')
      }

      // Get the CSV data and trigger download
      const blob = await response.blob()
      const downloadUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = downloadUrl
      link.download = `patients_export_${new Date().toISOString().split('T')[0]}.csv`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(downloadUrl)
    } catch (error) {
      console.error('Export failed:', error)
      alert('Failed to export CSV. Please try again.')
    }
  }

  const renderSortIcon = (column: string) => {
    if (filters.sort !== column) return null
    return filters.direction === 'asc' ? (
      <ChevronUpIcon className="ml-2 h-4 w-4 inline" />
    ) : (
      <ChevronDownIcon className="ml-2 h-4 w-4 inline" />
    )
  }

  const getCaseStatusBadgeVariant = (status: string | null): "default" | "secondary" | "destructive" | "outline" => {
    if (!status) return "outline"

    switch (status) {
      case 'cured':
        return 'default'
      case 'on_tx':
        return 'secondary'
      case 'closed_negative':
        return 'outline'
      default:
        return 'secondary'
    }
  }

  const getRiskLevelBadgeVariant = (level: string | null): "default" | "secondary" | "destructive" | "outline" => {
    if (!level) return "outline"

    switch (level.toLowerCase()) {
      case 'low':
        return 'default'
      case 'moderate':
        return 'secondary'
      case 'high':
        return 'destructive'
      case 'critical':
        return 'destructive'
      default:
        return 'outline'
    }
  }

  return (
    <div className="flex min-h-screen w-full flex-col bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950">
      <div className="flex flex-col sm:gap-4 sm:py-4">
        <AppHeader breadcrumbs={[{ label: 'Patients' }]} />

        <main className="grid flex-1 items-start gap-4 p-4 sm:px-6 sm:py-0 md:gap-8">
          <Card className="shadow-lg border-0 bg-white/80 backdrop-blur-sm dark:bg-gray-900/80">
            <CardHeader className="bg-gradient-to-r from-blue-50/50 to-indigo-50/50 dark:from-blue-950/30 dark:to-indigo-950/30 border-b">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-gradient-to-br from-blue-600 to-indigo-600 shadow-md">
                    <UsersIcon className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <CardTitle className="text-2xl font-bold">Patients</CardTitle>
                    <p className="text-sm text-muted-foreground mt-1">Manage and view all patient records</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={handleExportCSV}
                    variant="outline"
                    className="border-blue-200 dark:border-blue-800"
                  >
                    <DownloadIcon className="mr-2 h-4 w-4" />
                    Export CSV
                  </Button>
                  <Button
                    onClick={() => router.visit('/admin/patients/new')}
                    className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
                  >
                    <PlusIcon className="mr-2 h-4 w-4" />
                    New Patient
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-6">
              {/* Search and Filters */}
              <div className="mb-6 rounded-lg bg-gradient-to-br from-blue-50/30 to-indigo-50/30 dark:from-blue-950/20 dark:to-indigo-950/20 p-4 border border-blue-100 dark:border-blue-900/30">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
                  <div className="relative flex-1">
                    <SearchIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-blue-600 dark:text-blue-400" />
                    <Input
                      type="text"
                      placeholder="Search by name, phone, or email..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 border-blue-200 dark:border-blue-800 focus:border-blue-400 dark:focus:border-blue-600"
                    />
                  </div>

                  <Select
                    value={filters.site_id?.toString() || 'all'}
                    onValueChange={(value) => handleFilterChange('site_id', value === 'all' ? undefined : value)}
                  >
                    <SelectTrigger className="w-full sm:w-[200px] border-blue-200 dark:border-blue-800">
                      <SelectValue placeholder="All Sites" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Sites</SelectItem>
                      {sites.map((site) => (
                        <SelectItem key={site.id} value={site.id.toString()}>
                          {site.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {hasActiveFilters && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={clearFilters}
                      className="w-full sm:w-auto border-blue-200 dark:border-blue-800 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 dark:hover:from-blue-950/50 dark:hover:to-indigo-950/50 transition-all"
                    >
                      <XIcon className="mr-2 h-4 w-4" />
                      Clear Filters
                    </Button>
                  )}
                </div>
              </div>

              {/* Results Count */}
              <div className="mb-4 text-sm font-medium text-muted-foreground">
                Showing <span className="text-blue-600 dark:text-blue-400">{pagination.from}</span> to <span className="text-blue-600 dark:text-blue-400">{pagination.to}</span> of <span className="text-blue-600 dark:text-blue-400">{pagination.count}</span> patients
              </div>

              {/* Patients Table */}
              <div className="rounded-lg border border-blue-100 dark:border-blue-900/30 overflow-hidden shadow-sm">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/50 dark:to-indigo-950/50 hover:from-blue-50 hover:to-indigo-50 dark:hover:from-blue-950/50 dark:hover:to-indigo-950/50">
                      <TableHead className="cursor-pointer font-semibold" onClick={() => handleSort('legal_name')}>
                        Name {renderSortIcon('legal_name')}
                      </TableHead>
                      <TableHead className="font-semibold">Contact</TableHead>
                      <TableHead className="cursor-pointer font-semibold" onClick={() => handleSort('site_id')}>
                        Site {renderSortIcon('site_id')}
                      </TableHead>
                      <TableHead className="font-semibold">Care Status</TableHead>
                      <TableHead className="font-semibold">Activity</TableHead>
                      <TableHead className="cursor-pointer font-semibold" onClick={() => handleSort('created_at')}>
                        Added {renderSortIcon('created_at')}
                      </TableHead>
                      <TableHead className="font-semibold">Risk Level</TableHead>
                      <TableHead className="font-semibold">Days at Stage</TableHead>
                      <TableHead className="font-semibold">Upcoming</TableHead>
                      <TableHead className="font-semibold">Treatment Plan</TableHead>
                      {auth.user.admin && <TableHead className="font-semibold text-right">Actions</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {patients.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={auth.user.admin ? 11 : 10} className="text-center py-12">
                          <div className="flex flex-col items-center gap-4">
                            <div className="flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-blue-100 to-indigo-100 dark:from-blue-950 dark:to-indigo-950">
                              <UsersIcon className="h-10 w-10 text-blue-600 dark:text-blue-400" />
                            </div>
                            <div>
                              <p className="text-lg font-semibold text-foreground">No patients found</p>
                              <p className="text-sm text-muted-foreground mt-1">Try adjusting your search or filters</p>
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : (
                      patients.map((patient) => (
                        <TableRow key={patient.id} className="transition-all hover:bg-gradient-to-r hover:from-blue-50/50 hover:to-indigo-50/50 dark:hover:from-blue-950/30 dark:hover:to-indigo-950/30">
                          <TableCell className="py-4">
                            <div
                              className="flex items-center gap-3 cursor-pointer group"
                              onClick={() => router.visit(`/admin/patients/${patient.id}`)}
                            >
                              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-indigo-500 shadow-sm group-hover:shadow-md transition-shadow">
                                <UserIcon className="h-4 w-4 text-white" />
                              </div>
                              <div>
                                <div className="font-semibold text-blue-600 dark:text-blue-400 group-hover:underline">{patient.display_name}</div>
                                {patient.legal_name !== patient.display_name && (
                                  <div className="text-xs text-muted-foreground">({patient.legal_name})</div>
                                )}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="py-4">
                            <div className="text-sm">
                              <div className="font-medium">{patient.phone_number}</div>
                              {patient.email && (
                                <div className="text-xs text-muted-foreground">{patient.email}</div>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="py-4">
                            <span className="text-sm font-medium">{patient.site_name}</span>
                          </TableCell>
                          <TableCell className="py-4">
                            {patient.case_status ? (
                              <Badge
                                variant={getCaseStatusBadgeVariant(patient.case_status)}
                                className="shadow-sm font-medium"
                              >
                                {patient.case_status_label}
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="shadow-sm font-medium">No Case</Badge>
                            )}
                          </TableCell>
                          <TableCell className="py-4">
                            {patient.days_since_last_activity !== null ? (
                              <span className="text-sm font-medium text-muted-foreground">
                                {patient.days_since_last_activity === 0 ? 'Today' : `${patient.days_since_last_activity}d ago`}
                              </span>
                            ) : (
                              <span className="text-sm text-muted-foreground">—</span>
                            )}
                          </TableCell>
                          <TableCell className="py-4">
                            <span className="text-sm text-muted-foreground">
                              {new Date(patient.created_at).toLocaleDateString()}
                            </span>
                          </TableCell>
                          <TableCell className="py-4">
                            {patient.risk_level ? (
                              <Badge
                                variant={getRiskLevelBadgeVariant(patient.risk_level)}
                                className="shadow-sm font-medium"
                              >
                                {patient.risk_level_label}
                              </Badge>
                            ) : (
                              <span className="text-sm text-muted-foreground">—</span>
                            )}
                          </TableCell>
                          <TableCell className="py-4">
                            {patient.days_at_current_stage !== null ? (
                              <span className="text-sm font-medium text-muted-foreground">
                                {patient.days_at_current_stage === 0 ? 'Today' : `${patient.days_at_current_stage}d`}
                              </span>
                            ) : (
                              <span className="text-sm text-muted-foreground">—</span>
                            )}
                          </TableCell>
                          <TableCell className="py-4">
                            <div className="flex items-center gap-2">
                              <CalendarIcon className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                              <span className="text-sm font-medium">{patient.upcoming_appointments_count}</span>
                            </div>
                          </TableCell>
                          <TableCell className="py-4">
                            <div className="flex items-center gap-2">
                              {patient.has_treatment_plan_instructions ? (
                                <>
                                  <CheckIcon className="h-4 w-4 text-green-600 dark:text-green-400" />
                                  <span className="text-sm font-medium text-green-600 dark:text-green-400">Yes</span>
                                </>
                              ) : (
                                <span className="text-sm text-muted-foreground">No</span>
                              )}
                            </div>
                          </TableCell>
                          {auth.user.admin && (
                            <TableCell className="py-4 text-right">
                              <Button
                                variant="ghost"
                                size="icon"
                                aria-label="Reset password"
                                onClick={(e) => handleResetPasswordClick(patient, e)}
                              >
                                <KeyRoundIcon className="size-4" />
                              </Button>
                            </TableCell>
                          )}
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {pagination.pages > 1 && (
                <div className="mt-6">
                  <Pagination>
                    <PaginationContent>
                      <PaginationItem>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handlePageChange(pagination.page - 1)}
                          disabled={!pagination.prev}
                          className="border-blue-200 dark:border-blue-800 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 dark:hover:from-blue-950/50 dark:hover:to-indigo-950/50"
                        >
                          <ChevronLeftIcon className="h-4 w-4 mr-2" />
                          Previous
                        </Button>
                      </PaginationItem>

                      {[...Array(pagination.pages)].map((_, index) => {
                        const pageNum = index + 1
                        if (
                          pageNum === 1 ||
                          pageNum === pagination.pages ||
                          (pageNum >= pagination.page - 1 && pageNum <= pagination.page + 1)
                        ) {
                          return (
                            <PaginationItem key={pageNum}>
                              <Button
                                variant={pageNum === pagination.page ? 'default' : 'outline'}
                                size="sm"
                                onClick={() => handlePageChange(pageNum)}
                                className={pageNum === pagination.page
                                  ? 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-md'
                                  : 'border-blue-200 dark:border-blue-800 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 dark:hover:from-blue-950/50 dark:hover:to-indigo-950/50'}
                              >
                                {pageNum}
                              </Button>
                            </PaginationItem>
                          )
                        } else if (pageNum === pagination.page - 2 || pageNum === pagination.page + 2) {
                          return <PaginationEllipsis key={pageNum} />
                        }
                        return null
                      })}

                      <PaginationItem>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handlePageChange(pagination.page + 1)}
                          disabled={!pagination.next}
                          className="border-blue-200 dark:border-blue-800 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 dark:hover:from-blue-950/50 dark:hover:to-indigo-950/50"
                        >
                          Next
                          <ChevronRightIcon className="h-4 w-4 ml-2" />
                        </Button>
                      </PaginationItem>
                    </PaginationContent>
                  </Pagination>
                </div>
              )}
            </CardContent>
          </Card>
        </main>
      </div>

      {patientToResetPassword && (
        <ResetPasswordDialog
          open={resetPasswordDialogOpen}
          onOpenChange={setResetPasswordDialogOpen}
          targetName={patientToResetPassword.display_name}
          resetUrl={`/admin/patients/${patientToResetPassword.id}/reset_password`}
        />
      )}
    </div>
  )
}
